/**
 * Common UI utilities.
 *
 * `cn` is the shadcn-pattern className composer: merges Tailwind classes
 * intelligently so later utility wins (`p-2 p-4` becomes `p-4`).
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { TaskPriority } from '@workspace/shared';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Today's date in the local timezone, formatted as `YYYY-MM-DD`.
 *
 * Used by surfaces that compare against the wire-format date string the
 * backend returns for `dueDate`, `frogDates`, and the morning-brief
 * `todayIso` field. Lifted out of `components/tasks/TaskCard.tsx` in
 * PR #35 so QuickCapture and TaskCard share one source of truth — both
 * need the same canonical "today" anchor when defaulting due dates.
 *
 * Returns the local-timezone date rather than UTC because the backend
 * itself treats `dueDate` as a calendar date in the user's timezone,
 * not a UTC timestamp.
 */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Period boundary used by the Tasks Table filter (UAT-48).
 *
 * Returns the ISO date (`YYYY-MM-DD`) of the first day of the selected
 * period in Brisbane local time. `'all'` returns the earliest
 * representable date (`1970-01-01`) so the backend `since` filter passes
 * every completed row through and the front-end open-task comparator
 * still has a sortable string to compare against.
 *
 * Brisbane is fixed at +10:00 year-round (no daylight saving) so the
 * function uses `toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })`
 * to anchor the calculation away from the host machine's timezone — the
 * dev laptop may sit in a different zone, and the production server is
 * UTC.
 */
export function startOfPeriod(period: 'all' | 'year' | 'month' | 'week' | 'today'): string {
  const now = new Date();
  // Re-anchor `now` to Brisbane wall-clock time so the day / month / year
  // calculations below operate on the timezone the user actually lives in.
  const brisbane = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  switch (period) {
    case 'today': {
      return formatIso(brisbane);
    }
    case 'week': {
      // Most recent Monday (ISO week start). `getDay()` returns 0 for
      // Sunday and 1 for Monday, so the `(day + 6) % 7` shift turns
      // Monday into 0 and Sunday into 6.
      const d = new Date(brisbane);
      const day = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - day);
      return formatIso(d);
    }
    case 'month': {
      return formatIso(new Date(brisbane.getFullYear(), brisbane.getMonth(), 1));
    }
    case 'year': {
      return formatIso(new Date(brisbane.getFullYear(), 0, 1));
    }
    case 'all':
    default:
      return '1970-01-01';
  }
}

/** Local helper: format a Date as `YYYY-MM-DD` without leaking the
 * host-machine's timezone offset (toISOString would). */
function formatIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * MAXART priority colour map for inline text styling (foreground only).
 *
 * PR #38 overhauled the priority palette and at the same time moved
 * the canonical priority colour onto the new `<PriorityPill />`
 * component (filled background + white text). To keep priority chips
 * looking the same across every surface, this helper now returns a
 * neutral `text-mx-white` for every priority — surfaces that want the
 * priority colour render the pill instead, which carries the tone on
 * its background. Returning a single class also keeps the slide-over's
 * priority select legible across the new HIGH (warm orange) and
 * MEDIUM (cool navy) tones without forcing the text to compete with
 * the pill colour.
 *
 * Lifted out of `components/shared/SlideOver.tsx` in PR #31 so the
 * card and slide-over share one source of truth.
 */
export function priorityClass(_priority: TaskPriority): string {
  return 'text-mx-white';
}
